import React, { useState, useEffect, useRef } from "react";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { Input, Button } from "antd";
import ReactMarkdown from "react-markdown";
import { routes } from "api/routes";
import PropTypes from "prop-types";
import { apiGetChatMessages } from "api/apiAgent";
import { toFixed } from "utils/helpers";

const calculatePrice = (tokens, rate) => {
  return (tokens / 1000000) * rate;
};

const calculateTokenUsage = (type, tokenUsage) => {
  if (type === "input") {
    const uncachedInputTokensPrice = calculatePrice(
      tokenUsage.input_tokens - tokenUsage.input_cache_tokens,
      0.25
    );
    const cachedInputTokensPrice = calculatePrice(
      tokenUsage.input_cache_tokens,
      0.025
    );
    return toFixed(uncachedInputTokensPrice + cachedInputTokensPrice, 5);
  } else if (type === "output") {
    return toFixed(calculatePrice(tokenUsage.output_tokens, 2), 5);
  }
};

const TokenUsage = React.memo(({ tokenUsage }) => {
  if (!tokenUsage) return null;
  return (
    <div className="flex items-center justify-end mt-2">
      <div className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
        Input tokens: {tokenUsage.input_tokens} ($
        {calculateTokenUsage("input", tokenUsage)}) | Output tokens:{" "}
        {tokenUsage.output_tokens} ($
        {calculateTokenUsage("output", tokenUsage)})
      </div>
    </div>
  );
});

const AIChat = ({
  selectedConversation,
  updateLastMessageTime,
  fetchConversations,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingSteps, setStreamingSteps] = useState([]);
  const [finalAnswer, setFinalAnswer] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [tokenUsage, setTokenUsage] = useState(null);

  const messagesContainerRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: input.trim(),
      createdAt: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      setIsStreaming(true);
      setStreamingSteps([]);
      setFinalAnswer("");
      setTokenUsage(null);

      // Start streaming response
      const response = await fetch(routes.streamChat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify({
          message: userMessage.content,
          chatId: selectedConversation._id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullMessage = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();

            if (data === "[DONE]") {
              // Stream completed
              const assistantMessage = {
                role: "assistant",
                content: fullMessage,
                createdAt: new Date(),
                tokenUsage: tokenUsage,
              };

              setMessages((prev) => [...prev, assistantMessage]);
              setStreamingSteps([]);
              setTokenUsage(null);
              setFinalAnswer("");
              setIsStreaming(false);
              return;
            }

            try {
              const parsed = JSON.parse(data);

              if (parsed.type === "content") {
                fullMessage += parsed.content;
                setFinalAnswer(fullMessage);
              } else if (parsed.type === "token_usage") {
                const usage = {
                  input_tokens: parsed.usage.input_tokens,
                  input_cache_tokens: parsed.usage.input_cache_tokens,
                  output_tokens: parsed.usage.output_tokens,
                };
                setTokenUsage(usage);
                updateLastMessageTime(selectedConversation._id, usage);
              } else if (parsed.type === "tool_start") {
                // Add new tool step
                const newStep = {
                  id: Date.now(),
                  tool: parsed.tool,
                  status: "running",
                  createdAt: new Date(),
                };
                setStreamingSteps((prev) => [...prev, newStep]);
              } else if (parsed.type === "tool_end") {
                // Mark tool step as completed
                setStreamingSteps((prev) =>
                  prev.map((step) =>
                    step.tool === parsed.tool && step.status === "running"
                      ? { ...step, status: "completed" }
                      : step
                  )
                );
                if (parsed.tool === "set_conversation_title") {
                  fetchConversations();
                }
              } else if (parsed.type === "summarizing") {
                // Add memory optimization step
                const newStep = {
                  id: Date.now(),
                  tool: "memory",
                  message: parsed.message,
                  status: "running",
                  createdAt: new Date(),
                };
                setStreamingSteps((prev) => [...prev, newStep]);
              } else if (parsed.type === "summarizing_complete") {
                // Mark memory optimization as completed
                setStreamingSteps((prev) =>
                  prev.map((step) =>
                    step.tool === "memory" && step.status === "running"
                      ? {
                          ...step,
                          status: "completed",
                          message: parsed.message,
                        }
                      : step
                  )
                );
              } else if (parsed.type === "error") {
                throw new Error(parsed.error);
              }
            } catch (parseError) {
              console.warn("Failed to parse streaming data:", data);
            }
          }
        }
      }
    } catch (error) {
      console.error(error);
      const errorMessage = {
        role: "assistant",
        content:
          "Sorry, I encountered an error while processing your request. Please try again.",
        createdAt: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setStreamingSteps([]);
      setFinalAnswer("");
    } finally {
      setLoading(false);
      setIsStreaming(false);
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchMessages = async () => {
      const messages = await apiGetChatMessages(selectedConversation._id);
      setMessages(messages);
    };
    fetchMessages();
  }, [selectedConversation]);

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <GenericCard
      maxHeight={1000}
      title={selectedConversation?.title || "New Conversation"}
      disableToggle
    >
      <div
        ref={messagesContainerRef}
        className="h-[530px] overflow-y-auto space-y-4"
      >
        {[
          {
            role: "assistant",
            content:
              "Hi! I'm your AI assistant for creating stock prediction models. I have access to the latest available symbols and sectors from the system and can help you generate optimized model configurations.\n\n💡 **I can help with:**\n✓ Sector-based selections (tech, energy, finance, etc.)\n✓ Specific ticker requests\n✓ Random diversified portfolios\n✓ Custom technical indicators\n✓ Different model types and resolutions\n\nWhat would you like to create?",
            createdAt: new Date(),
          },
          ...messages,
        ].map((message, index) => (
          <div
            key={index}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                message.role === "user"
                  ? "bg-[#3b82f6] dark:bg-[#2563eb] text-white"
                  : "bg-[#f3f4f6] dark:bg-[#374151] text-[#111827] dark:text-[#f9fafb]"
              }`}
            >
              <div
                className={`prose prose-sm max-w-none ${
                  message.role === "user" ? "prose-invert" : "dark:prose-invert"
                }`}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <p className="mb-2 last:mb-0">{children}</p>
                    ),
                    ul: ({ children }) => (
                      <ul className="mb-2 last:mb-0 pl-4">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="mb-2 last:mb-0 pl-4">{children}</ol>
                    ),
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    h1: ({ children }) => (
                      <h1 className="text-lg font-bold mb-2">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-base font-bold mb-2">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-sm font-bold mb-1">{children}</h3>
                    ),
                    code: ({ inline, children }) =>
                      inline ? (
                        <code className="px-1 py-0.5 rounded bg-black bg-opacity-10 text-sm font-mono">
                          {children}
                        </code>
                      ) : (
                        <pre className="p-2 rounded bg-black bg-opacity-10 text-sm font-mono overflow-x-auto mb-2">
                          <code>{children}</code>
                        </pre>
                      ),
                    strong: ({ children }) => (
                      <strong className="font-bold">{children}</strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic">{children}</em>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>

              {(tokenUsage || message.tokenUsage) && (
                <TokenUsage tokenUsage={message.tokenUsage} />
              )}
              <div className="flex items-center justify-between mt-2">
                <div
                  className={`text-xs ${
                    message.role === "user"
                      ? "text-[#dbeafe] dark:text-[#bfdbfe]"
                      : "text-[#6b7280] dark:text-[#9ca3af]"
                  }`}
                >
                  {formatTime(message.createdAt)}
                </div>
              </div>
            </div>
          </div>
        ))}

        {(loading || isStreaming) && (
          <div className="flex justify-start">
            <div className="max-w-[80%] bg-[#f3f4f6] dark:bg-[#374151] text-[#111827] dark:text-[#f9fafb] rounded-lg px-4 py-2">
              {isStreaming ? (
                <div className="space-y-3">
                  {/* Show tool usage steps */}
                  {streamingSteps.map((step) => (
                    <div
                      key={step.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      {step.status === "running" ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#3b82f6]"></div>
                          <span>
                            {step.tool === "memory" ? (
                              <>🧠 {step.message}</>
                            ) : (
                              <>🔧 Using {step.tool}...</>
                            )}
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="w-3 h-3 bg-[#10b981] rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                          <span>
                            {step.tool === "memory" ? (
                              <>🧠 {step.message}</>
                            ) : (
                              <>
                                🔧 Using {step.tool} -{" "}
                                <span className="text-[#10b981]">
                                  completed
                                </span>
                              </>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  ))}

                  {/* Show final answer if available */}
                  {finalAnswer && (
                    <div className="mt-4 pt-3 border-t border-[#e5e7eb] dark:border-[#4b5563]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium">
                          💬 Response:
                        </span>
                      </div>
                      <div className="prose prose-sm max-w-none dark:prose-invert">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <p className="mb-2 last:mb-0">{children}</p>
                            ),
                            ul: ({ children }) => (
                              <ul className="mb-2 last:mb-0 pl-4">
                                {children}
                              </ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="mb-2 last:mb-0 pl-4">
                                {children}
                              </ol>
                            ),
                            li: ({ children }) => (
                              <li className="mb-1">{children}</li>
                            ),
                            h1: ({ children }) => (
                              <h1 className="text-lg font-bold mb-2">
                                {children}
                              </h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="text-base font-bold mb-2">
                                {children}
                              </h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="text-sm font-bold mb-1">
                                {children}
                              </h3>
                            ),
                            code: ({ inline, children }) =>
                              inline ? (
                                <code className="px-1 py-0.5 rounded bg-black bg-opacity-10 text-sm font-mono">
                                  {children}
                                </code>
                              ) : (
                                <pre className="p-2 rounded bg-black bg-opacity-10 text-sm font-mono overflow-x-auto mb-2">
                                  <code>{children}</code>
                                </pre>
                              ),
                            strong: ({ children }) => (
                              <strong className="font-bold">{children}</strong>
                            ),
                            em: ({ children }) => (
                              <em className="italic">{children}</em>
                            ),
                          }}
                        >
                          {finalAnswer}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* Show thinking indicator if no steps yet */}
                  {streamingSteps.length === 0 && !finalAnswer && (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#111827] dark:border-[#f9fafb]"></div>
                      <span>Thinking...</span>
                    </div>
                  )}

                  {/* Streaming indicator */}
                  {(streamingSteps.length > 0 || finalAnswer) && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="animate-pulse w-2 h-2 bg-[#111827] dark:bg-[#f9fafb] rounded-full"></div>
                      <div className="animate-pulse w-2 h-2 bg-[#111827] dark:bg-[#f9fafb] rounded-full animation-delay-200"></div>
                      <div className="animate-pulse w-2 h-2 bg-[#111827] dark:bg-[#f9fafb] rounded-full animation-delay-400"></div>
                    </div>
                  )}

                  {/* Live token usage during streaming */}
                  {tokenUsage && <TokenUsage tokenUsage={tokenUsage} />}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#111827] dark:border-[#f9fafb]"></div>
                  <span>Thinking...</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="p-4 border-t border-[#e5e7eb] dark:border-[#374151]"
      >
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me to create a model... (e.g., 'Create a model for technology sector with 5% profit in 14-bar target')"
            disabled={loading || isStreaming}
            className="flex-1"
            size="large"
            onPressEnter={handleSubmit}
          />
          <Button
            type="primary"
            htmlType="submit"
            disabled={loading || isStreaming || !input.trim()}
            size="large"
            loading={loading || isStreaming}
          >
            Send
          </Button>
        </div>
      </form>
      <div>
        {selectedConversation.tokenUsage && (
          <div className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
            Total Cost: $
            {calculateTokenUsage("input", selectedConversation.tokenUsage) +
              calculateTokenUsage("output", selectedConversation.tokenUsage)}
          </div>
        )}
      </div>
    </GenericCard>
  );
};

export default AIChat;

AIChat.propTypes = {
  selectedConversation: PropTypes.object.isRequired,
  updateLastMessageTime: PropTypes.func.isRequired,
  fetchConversations: PropTypes.func.isRequired,
};
